import { theme } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { useEffect } from "react";
import type { PreloadedQuery } from "react-relay";
import { graphql, useFragment, usePreloadedQuery } from "react-relay";

import { useAppBarStickyHeightValue } from "~/components/app-bar/hooks";
import type { DefaultComponentsProvider } from "~/components/article/article-render";
import { query } from "~/components/article/article-render/live-article";
import { getItemContainerId } from "~/components/article/article-render/live-article/helpers";
import {
  useFilteredLiveContents,
  useTimelineScrollEffect,
} from "~/components/article/article-render/live-article/hooks";
import type { contentLiveArticleArticle$key } from "~/queries/__generated__/contentLiveArticleArticle.graphql";
import type { contentLiveArticleLiveArticle$key } from "~/queries/__generated__/contentLiveArticleLiveArticle.graphql";
import type { liveArticleQuery } from "~/queries/__generated__/liveArticleQuery.graphql";

import { Container, StyledLiveArticleContentItem } from "./styles";

type Props = {
  className?: string;
  provider: DefaultComponentsProvider;
  queryReference: PreloadedQuery<liveArticleQuery>;
  reference: contentLiveArticleArticle$key;
};

export const LiveArticleContent: FunctionComponent<Props> = ({
  className,
  provider,
  queryReference,
  reference,
}) => {
  const data = usePreloadedQuery<liveArticleQuery>(query, queryReference);
  const { liveArticleScroller, scrollToElement } = useTimelineScrollEffect();
  const { appBarStickyHeight } = useAppBarStickyHeightValue();

  const article = useFragment(
    graphql`
      fragment contentLiveArticleArticle on Article {
        ...itemLiveArticleContentArticle
      }
    `,
    reference,
  );

  useEffect(() => {
    const type = "content";
    if (
      !liveArticleScroller.toScrollEntityId ||
      liveArticleScroller.toScrollType !== type ||
      appBarStickyHeight < 1
    )
      return;
    scrollToElement(type, {
      duration: 1000,
      offset: -(appBarStickyHeight + 3),
      smooth: true, // Scrolls to element + 50 pixels down the page
    });
  }, [scrollToElement, liveArticleScroller, appBarStickyHeight]);

  const content = useFragment<contentLiveArticleLiveArticle$key>(
    graphql`
      fragment contentLiveArticleLiveArticle on LiveArticle {
        status
      }
    `,
    data.liveArticle,
  );

  const filteredLiveContents = useFilteredLiveContents(data.liveArticle);

  return (
    <Container className={className}>
      {filteredLiveContents.map((liveContent, index) => (
        <>
          {liveContent && (
            <StyledLiveArticleContentItem
              id={getItemContainerId("content", liveContent.entityId ?? "")}
              isTheEnd={index === 0 && content?.status === false}
              key={liveContent.customTime}
              liveArticleReference={article}
              liveContentReference={liveContent}
              provider={provider}
            />
          )}
          {index === 3 && (
            <div key="inline-ad-1">
              {provider.adslot({
                adUnit: "m_lrec1",
                breakpoint: theme.breakpoints.down("desktop"),
                sizes: [[300, 250], [300, 600], [320, 480], "fluid"],
                withBackground: false,
                withLabel: true,
              })}
            </div>
          )}

          {index === 13 && (
            <div key="inline-ad-2">
              {provider.adslot({
                adUnit: "d_lrec2",
                breakpoint: theme.breakpoints.down("desktop"),
                sizes: [[300, 250], [300, 600], [320, 480], "fluid"],
                withBackground: false,
                withLabel: true,
              })}
            </div>
          )}
        </>
      ))}
    </Container>
  );
};

LiveArticleContent.displayName = "LiveArticleContent";
